import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { GtmService } from '../../../infrastructure/analytics/GtmService';

const CarouselContainer = styled.div`
  width: 100%;
  margin: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CarouselInner = styled.div`
  width: 100%;
  height: 400px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    max-height: 300px;
  }
`;

const CarouselItem = styled.div`
  width: 100%;
  height: 100%;
  display: ${({ $isActive }) => ($isActive ? 'block' : 'none')};
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 20px;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    max-height: 100%;
  }
`;

const CarouselLink = styled.a`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const IndicatorsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 15px;

  @media (max-width: 768px) {
    gap: 5px;
    margin-top: 10px;
  }
`;

const Indicator = styled.button`
  width: 15px;
  height: 15px;
  background-color: ${({ $isActive }) => ($isActive ? 'var(--bs-primary)' : 'white')};
  border-radius: 50%;
  border: 1px solid var(--bs-primary);
  cursor: pointer;
  transition: background-color 0.3s ease;
`;

export const BannerSliderUI = ({ banners }) => {
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [inViewport, setInViewport] = useState(false);
  const containerRef = useRef(null);
  // Impresiones ya contadas en este pageview: el autoplay (3s) daria impresiones
  // infinitas del mismo banner con la home abierta; una por banner y visita es lo
  // que espera el informe (view_promotion).
  const viewedRef = useRef(new Set());

  useEffect(() => {
    if (!containerRef.current || typeof IntersectionObserver === 'undefined') {
      setInViewport(true); // sin observer, mejor contar que no contar
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInViewport(entry.isIntersecting),
      { threshold: 0.5 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Impresion del slide activo: solo si el carrusel esta a la vista, la pestana
  // visible, y ese banner aun no se conto en este pageview.
  useEffect(() => {
    const banner = banners[activeItemIndex];
    if (!banner) return;
    if (!inViewport || document.visibilityState !== 'visible') return;
    const key = banner.id ?? banner.title ?? activeItemIndex;
    if (viewedRef.current.has(key)) return;
    viewedRef.current.add(key);
    GtmService.logViewPromotion(banner, 'carrusel', activeItemIndex + 1);
  }, [banners, activeItemIndex, inViewport]);

  // Auto-slide every 3 seconds
  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setActiveItemIndex((prevIndex) =>
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [banners.length, isHovered]);

  return (
    <CarouselContainer
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CarouselInner>
        {banners.map((banner, index) => (
          <CarouselItem
            key={banner.id ?? index}
            $isActive={index === activeItemIndex}
          >
            {!!banner.link ? (
              // data-banner alimenta el click_banner legado de index.html; el
              // select_promotion nuevo sale del onClick (conviven a proposito).
              <CarouselLink
                href={banner.link}
                target="_blank"
                rel="noopener noreferrer"
                data-banner={banner.title}
                onClick={() => GtmService.logSelectPromotion(banner, 'carrusel', index + 1)}
              >
                <CarouselImage src={banner.imageUrl} alt={banner.title} isClickable />
              </CarouselLink>
            ) : (
              <CarouselImage src={banner.imageUrl} alt={banner.title} />
            )}
          </CarouselItem>
        ))}
      </CarouselInner>

      <IndicatorsContainer>
        {banners.map((_, index) => (
          <Indicator
            key={index}
            $isActive={index === activeItemIndex}
            onClick={() => setActiveItemIndex(index)}
          />
        ))}
      </IndicatorsContainer>
    </CarouselContainer>
  );
};
